import { computed } from "vue";
import { storeToRefs } from "pinia";
import { useGridRowsStore, type GridChoice, type GridColumnOption } from "@stores/survey/grids/gridRows";
import { useGridsStore } from "@stores/survey/grids/grids";
import { useSurveyStore } from "@stores/survey/survey";
import { toggleInArray } from "@/utils/toggleArrayValue";
import type { FilterConfig, FilterValues } from "@/components/episto-ui/filter-dropdown";

export const useGridFilters = () => {
  const gridRowsStore = useGridRowsStore();
  const gridsStore = useGridsStore();
  const surveyStore = useSurveyStore();

  const { columnOptions, columnFilters } = storeToRefs(gridRowsStore);
  const { setFilters } = gridRowsStore;
  const { id: surveyId } = storeToRefs(surveyStore);

  const columns = computed(() => columnOptions.value as GridColumnOption[]);
  const filters = computed(() => columnFilters.value as Record<string | number, string[]>);

  const choiceLabel = (choice: GridChoice) =>
    choice.title || Object.values(choice.localized_title || {}).find((title) => title) || "";

  const getOptions = (choices: Record<string, GridChoice>) =>
    Object.entries(choices)
      .sort(([, a], [, b]) => a.rank - b.rank)
      .map(([key, choice]) => ({ value: key, label: choiceLabel(choice) }));

  const isCountry = (col: GridColumnOption) => col.question_name?.toLowerCase().includes("country");
  const hasChoices = (col: GridColumnOption) => col.choices && Object.keys(col.choices).length > 0;
  const getSelected = (id: number) => filters.value[id] || filters.value[String(id)] || [];

  const countryColumn = computed(() => columns.value.find((col) => isCountry(col) && hasChoices(col)));
  const isInGrid = (col: GridColumnOption) => gridsStore.hasColumn(col.id);
  const isFiltered = (col: GridColumnOption) => getSelected(col.id).length > 0;
  const otherColumns = computed(() =>
    columns.value.filter((col) => (isInGrid(col) || isFiltered(col)) && hasChoices(col) && !isCountry(col)),
  );

  const filterConfigs = computed<FilterConfig[]>(() => [
    ...(countryColumn.value
      ? [
          {
            id: String(countryColumn.value.id),
            label: "Country",
            hasSearch: true,
            options: getOptions(countryColumn.value.choices),
            selectedValues: getSelected(countryColumn.value.id),
          },
        ]
      : []),
    ...otherColumns.value.map((col) => ({
      id: String(col.id),
      label: col.question_name,
      hasSearch: true,
      options: getOptions(col.choices),
      selectedValues: getSelected(col.id),
    })),
  ]);

  const activeFiltersCount = computed(() =>
    Object.values(filters.value).reduce((sum, arr) => sum + (Array.isArray(arr) ? arr.length : 0), 0),
  );

  const persist = (data: FilterValues) => {
    if (surveyId.value) localStorage.setItem(`survey_filters_${surveyId.value}`, JSON.stringify(data));
  };

  const handleFilterToggle = async (filterId: string, value: string | boolean | number) => {
    const current = (filters.value[filterId] as (string | boolean | number)[]) || [];
    const updated = toggleInArray(current, value) as string[];
    const newFilters: Record<string, string[]> = { ...filters.value, [filterId]: updated };
    if (!updated.length) delete newFilters[filterId];
    await setFilters(newFilters);
    persist(newFilters);
  };

  const handleClear = async () => {
    await setFilters({});
    persist({});
  };

  const loadFiltersFromStorage = async () => {
    if (!surveyId.value) return false;
    const saved = localStorage.getItem(`survey_filters_${surveyId.value}`);
    if (!saved) return false;

    try {
      await setFilters(JSON.parse(saved));
      return true;
    } catch {
      console.error("Failed to parse saved filters");
      return false;
    }
  };

  return { filterConfigs, activeFiltersCount, handleFilterToggle, handleClear, loadFiltersFromStorage };
};
